import type { Cents } from './types';

/**
 * Money. Integer LKR cents everywhere, matching the backend contract exactly.
 * Rupees exist only at the two edges: what a person types, and what is drawn
 * on screen.
 */

export const MAX_CENTS = Number.MAX_SAFE_INTEGER;

/**
 * Parses what a user typed into cents, without ever building a float.
 *
 * Returns `null` rather than throwing for input that is not a number at all
 * ("", "abc", "-5"), because this runs on every keystroke and a half-typed
 * amount is normal, not an error.
 *
 * A trailing dot is treated as a complete value: "1." is 100 cents. Returning
 * null there would make the field drop to zero the instant the user pressed
 * the decimal point, then jump back — so the partial state is resolved
 * downward instead of discarded.
 */
export function parseRupeeInput(input: string): Cents | null {
  const text = input.trim().replace(/,/g, '');
  if (text === '') return null;

  const match = /^(\d+)(?:\.(\d{0,2}))?$/.exec(text);
  if (!match) return null;

  const [, whole, fraction] = match;
  const cents = Number(whole) * 100 + Number((fraction ?? '').padEnd(2, '0'));

  return Number.isSafeInteger(cents) ? cents : null;
}

/** True when the text could still become a valid amount as the user types. */
export function isPartialRupeeInput(input: string): boolean {
  return /^\d*(\.\d{0,2})?$/.test(input.trim().replace(/,/g, ''));
}

/** 12345678 -> "123,456.78" */
export function formatCents(cents: Cents): string {
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const rupees = Math.floor(abs / 100);
  const remainder = abs % 100;
  return `${negative ? '-' : ''}${rupees.toLocaleString('en-US')}.${String(remainder).padStart(2, '0')}`;
}

/** With the currency prefix, for headline figures. */
export function formatMoney(cents: Cents): string {
  return `LKR ${formatCents(cents)}`;
}

/**
 * Whole rupees when the cents are zero — the way the paper form is written.
 * Keeps the cents when they are not zero rather than rounding a printed figure.
 */
export function formatCentsForReport(cents: Cents | null): string {
  if (cents === null || cents === 0) return '';
  return cents % 100 === 0
    ? Math.floor(cents / 100).toLocaleString('en-US')
    : formatCents(cents);
}

/** Short form for chart axes and tiles: 1.3M, 45.2K. */
export function formatCompact(cents: Cents): string {
  const rupees = cents / 100;
  if (Math.abs(rupees) >= 1_000_000) return `${(rupees / 1_000_000).toFixed(1)}M`;
  if (Math.abs(rupees) >= 1_000) return `${(rupees / 1_000).toFixed(1)}K`;
  return rupees.toFixed(0);
}

/** For putting a cents value back into an editable text field. */
export function centsToInputValue(cents: Cents | null): string {
  if (cents === null || cents === 0) return '';
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2);
}

export function sumCents(values: readonly Cents[]): Cents {
  return values.reduce((total, value) => total + value, 0);
}
